// KPI calculations + formatting helpers.
// If a number looks wrong on the dashboard, start here.

import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import type { Project, SurveyResponse, MonthlyLedgerEntry, RiskCategory } from '../types'
import { TIERS } from './constants'
import { yearTotal, yearToDateTotal, yearRemainingTotal } from './ledger'
import { hasMaterialOpenRisk, DEFAULT_RISK_FORECAST_MIN_SCORE } from './risk'

// Classname helper used by shadcn components (src/components/ui/*).
// Merges Tailwind classes and dedupes conflicts (e.g. p-2 + p-4 -> p-4).
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Short random string used as the survey URL token, e.g. /survey/a1b2c3d4
export const generateToken = () => Math.random().toString(36).substring(2, 10)

// Unique project ID - timestamp + random suffix to avoid collisions
export const generateId = () => 'proj_' + Date.now().toString(36) + Math.random().toString(36).substring(2, 5)

// Per-project adoption % or `null` if the project doesn't contribute to the
// Adoption KPI (explicit 'na' mode, or missing inputs). Used both by the
// headline KPI math and by Dashboard/Reports per-project displays so every
// surface agrees on which projects count.
//
// Modes (see types.ts / USER_GUIDE §6.5):
//   - 'users'   (default): actualUsers ÷ expectedUsers, capped at 100
//   - 'percent'          : use adoptionRate as-is
//   - 'na'               : excluded
export const projectAdoptionPct = (p: Project): number | null => {
  const mode = p.adoptionMode || 'users'
  if (mode === 'na') return null
  if (mode === 'percent') {
    if (p.adoptionRate == null) return null
    return Math.min(100, Math.max(0, Math.round(p.adoptionRate)))
  }
  // 'users'
  if (p.actualUsers == null || !p.expectedUsers) return null
  return Math.min(100, Math.round((p.actualUsers / p.expectedUsers) * 100))
}

// Per-project "effective actual users" used by the sum-then-divide roll-up.
// For 'percent' projects we back-calculate it from adoptionRate × expectedUsers
// so percent-mode and users-mode projects live in the same formula and a
// 10-user project can't silently drag down a 1,000-user project's weight.
// Returns null when the project should be excluded entirely.
const effectiveAdoptionUsers = (p: Project): { actual: number; expected: number } | null => {
  const mode = p.adoptionMode || 'users'
  if (mode === 'na') return null
  if (!p.expectedUsers) return null
  if (mode === 'percent') {
    if (p.adoptionRate == null) return null
    const pct = Math.min(100, Math.max(0, p.adoptionRate))
    return { actual: Math.round((pct / 100) * p.expectedUsers), expected: p.expectedUsers }
  }
  // 'users'
  if (p.actualUsers == null) return null
  return { actual: p.actualUsers, expected: p.expectedUsers }
}

// Average score for one satisfaction question, ignoring unanswered (0) responses
const avgScore = (responses: SurveyResponse[], field: 'techSatisfaction' | 'peopleSatisfaction'): number => {
  const valid = responses.filter(r => r[field])
  if (valid.length === 0) return 0
  return Math.round(valid.reduce((sum, r) => sum + r[field], 0) / valid.length * 10) / 10
}

// Combined 1-5 score across both satisfaction questions.
// Formula: sum of all scores ÷ (responses × 2 questions)
export const calculateAverageSatisfactionScore = (responses: SurveyResponse[]): number => {
  if (responses.length === 0) return 0
  const totalPoints = responses.reduce((sum, r) => sum + r.techSatisfaction + r.peopleSatisfaction, 0)
  return Math.round(totalPoints / (responses.length * 2) * 10) / 10
}

// On Time / In Full / At Cost - the three delivery KPIs.
// onTimeDays and atCostPct come from Settings; defaults match the app's default tolerances.
function computeOTIFAC(projects: Project[], onTimeDays = 14, atCostPct = 10) {
  if (projects.length === 0) return { onTime: 0, inFull: 0, atCost: 0, count: 0 }

  const onTimeMs = onTimeDays * 24 * 60 * 60 * 1000
  const atCostMultiplier = 1 + atCostPct / 100

  const onTime = projects.filter(p =>
    p.actualGoLive && p.dueDate &&
    new Date(p.actualGoLive).getTime() - new Date(p.dueDate).getTime() <= onTimeMs
  ).length

  const inFull = projects.filter(p => p.deliveredInFull === 'Y').length

  // Only count projects where we have actual cost data - missing data doesn't penalise
  const atCost = projects.filter(p =>
    p.actualCost !== undefined && p.actualCost <= p.forecastCost * atCostMultiplier
  ).length

  return {
    onTime: Math.round((onTime / projects.length) * 100),
    inFull: Math.round((inFull / projects.length) * 100),
    atCost: Math.round((atCost / projects.length) * 100),
    count: projects.length,
  }
}

/** True when the project has an OPEN risk of the given category, ignoring
 *  severity. The Forecast KPIs use the severity-aware `hasMaterialOpenRisk`
 *  (lib/risk) instead; this stays as a simple category predicate. */
export function hasOpenRisk(p: Project, category: RiskCategory): boolean {
  return (p.risks ?? []).some(r => r.status === 'open' && r.category === category)
}

/** Projected On Time status for an in-flight project, judged from its own
 *  dates. A miss when the booked go-live already lands beyond dueDate + grace
 *  ('planned-late'), or - nothing booked in time - today has run past dueDate +
 *  grace ('overdue'). Planned-late is checked first because a stale plan whose
 *  date has already slipped past should fall back to the clock. Callers layer
 *  the delivery-risk signal on top. `reason` feeds the Forecast Outlook copy so
 *  the badge and its explanation can never disagree. */
export function forecastOnTime(
  project: Pick<Project, 'dueDate' | 'actualGoLive'>,
  onTimeMs: number,
  now: number = Date.now(),
): { late: boolean; reason: 'planned-late' | 'overdue' | null } {
  if (!project.dueDate) return { late: false, reason: null }
  const dueMs = new Date(project.dueDate).getTime()
  if (project.actualGoLive && new Date(project.actualGoLive).getTime() - dueMs > onTimeMs)
    return { late: true, reason: 'planned-late' }
  if (now - dueMs > onTimeMs) return { late: true, reason: 'overdue' }
  return { late: false, reason: null }
}

// Projected OTIFAC - the "Forecast" view of the three delivery KPIs.
// Blends completed actuals with in-flight projections so the number answers
// "where will we land if in-flight work goes the way it currently looks?":
//   - Completed projects score exactly as in computeOTIFAC (same rules,
//     including At Cost's all-projects denominator).
//   - In-flight (forecast-status) projects are optimistic by default and only
//     count as misses on a concrete signal. Each risk category maps to the KPI
//     it threatens, and only counts once it's material (effective rating >=
//     `riskMinScore`, Amber+ by default - see lib/risk hasMaterialOpenRisk):
//       On Time - the booked go-live already lands beyond dueDate + grace, OR
//                 today has run past dueDate + grace with none booked in time
//                 (see forecastOnTime), OR a material open 'delivery' risk
//       In Full - a material open 'scope' risk
//       At Cost - a material open 'cost' risk
// This means a project with no material risks, whose go-live sits within
// dueDate + grace, passes all three - the Forecast view is only as honest as
// its dates and risk log.
export function computeProjectedOTIFAC(
  completed: Project[],
  inFlight: Project[],
  onTimeDays = 14,
  atCostPct = 10,
  riskMinScore = DEFAULT_RISK_FORECAST_MIN_SCORE,
) {
  const total = completed.length + inFlight.length
  if (total === 0) return { onTime: 0, inFull: 0, atCost: 0, count: 0 }

  const onTimeMs = onTimeDays * 24 * 60 * 60 * 1000
  const atCostMultiplier = 1 + atCostPct / 100
  const now = Date.now()

  const completedOnTime = completed.filter(p =>
    p.actualGoLive && p.dueDate &&
    new Date(p.actualGoLive).getTime() - new Date(p.dueDate).getTime() <= onTimeMs
  ).length
  const inFlightOnTime = inFlight.filter(p =>
    !forecastOnTime(p, onTimeMs, now).late && !hasMaterialOpenRisk(p.risks, 'delivery', riskMinScore)
  ).length

  const completedInFull = completed.filter(p => p.deliveredInFull === 'Y').length
  const inFlightInFull = inFlight.filter(p => !hasMaterialOpenRisk(p.risks, 'scope', riskMinScore)).length

  const completedAtCost = completed.filter(p =>
    p.actualCost !== undefined && p.actualCost <= p.forecastCost * atCostMultiplier
  ).length
  const inFlightAtCost = inFlight.filter(p => !hasMaterialOpenRisk(p.risks, 'cost', riskMinScore)).length

  return {
    onTime: Math.round(((completedOnTime + inFlightOnTime) / total) * 100),
    inFull: Math.round(((completedInFull + inFlightInFull) / total) * 100),
    atCost: Math.round(((completedAtCost + inFlightAtCost) / total) * 100),
    count: total,
  }
}

// Realised KPIs - computed only over `status === 'complete'` projects.
// The 6 KPI tiles on Dashboard/Reports are fed from this.
// Callers MUST pass already-filtered (status==='complete') projects.
export function calculateRealisedKPIs(
  completed: Project[],
  surveyResponses: SurveyResponse[],
  onTimeDays = 14,
  atCostPct = 10,
) {
  const { onTime, inFull, atCost } = computeOTIFAC(completed, onTimeDays, atCostPct)

  const techSat = avgScore(surveyResponses, 'techSatisfaction')
  const peopleSat = avgScore(surveyResponses, 'peopleSatisfaction')
  const satisfaction = calculateAverageSatisfactionScore(surveyResponses)

  // Adoption: sum actual + expected users across all contributing projects
  // (sum-then-divide, not average-of-percentages). Percent-mode projects are
  // back-calculated into user counts via `effectiveAdoptionUsers` so they
  // can live in the same formula. 'na'-mode projects are excluded entirely.
  let totalActualUsers = 0
  let totalExpectedUsers = 0
  for (const p of completed) {
    const eff = effectiveAdoptionUsers(p)
    if (!eff) continue
    totalActualUsers += eff.actual
    totalExpectedUsers += eff.expected
  }
  const adoption = totalExpectedUsers > 0 ? Math.min(100, Math.round((totalActualUsers / totalExpectedUsers) * 100)) : 0

  let hrsSaved = 0
  const now = new Date()
  const curYear = now.getFullYear()
  const curMonth = now.getMonth() + 1
  for (const p of completed) {
    hrsSaved += realisedLedgerHrs(p.monthlyLedger, curYear, curMonth)
  }

  return { onTime, inFull, atCost, satisfaction, techSat, peopleSat, adoption, hrsSaved }
}

// Forecast / pipeline KPIs - computed only over `status === 'forecast'` projects.
// `pipelineHrs` is the headline number for the Reports headline tile (Y forecast).
export function calculateForecastKPIs(forecast: Project[]) {
  return {
    pipelineHrs: forecast.reduce((sum, p) => sum + (p.expectedValue || 0), 0),
    pipelineCost: forecast.reduce((sum, p) => sum + (p.forecastCost || 0), 0),
    pipelineUsers: forecast.reduce((sum, p) => sum + (p.expectedUsers || 0), 0),
    projectCount: forecast.length,
  }
}

/** Forecast projects whose dueDate has already passed - "silently late" because
 *  they never appear in On Time (which only measures completed projects). */
export function getLateProjects(projects: Project[]): Project[] {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return projects.filter(
    p => p.status === 'forecast' && p.dueDate && new Date(p.dueDate) < today
  )
}

// Total forecast hours across the whole portfolio = pipeline (forecast-status
// projects' expectedValue) + remaining ledger months on completed projects
// (current-year remainder + future years). The forecast on Dashboard's Hrs
// Saved tile and the product page. Reports' Projected Hour Savings applies the
// same rules year by year (valueByYear), limited to the goal window.
export function calculateTotalForecastHrs(completed: Project[], forecast: Project[]) {
  const now = new Date()
  const curYear = now.getFullYear()
  const curMonth = now.getMonth() + 1
  let total = forecast.reduce((s, p) => s + (p.expectedValue || 0), 0)
  for (const proj of completed) {
    const ledger = proj.monthlyLedger
    if (!ledger) continue
    const yrs = new Set<number>()
    for (const key of Object.keys(ledger)) {
      const y = Number(key.slice(0, 4))
      if (!Number.isNaN(y)) yrs.add(y)
    }
    for (const y of yrs) {
      if (y === curYear) total += yearRemainingTotal(ledger, y, curMonth)
      else if (y > curYear) total += yearTotal(ledger, y)
    }
  }
  return total
}

// Realised delivery KPIs broken out by tier - used in the "KPIs by Tier" chart.
// Callers MUST pass already-filtered (status==='complete') projects.
export function calculateKPIsByTier(completed: Project[], onTimeDays = 14, atCostPct = 10) {
  return TIERS.map(tier => ({
    tier,
    ...computeOTIFAC(completed.filter(p => p.tier === tier), onTimeDays, atCostPct),
  }))
}

// Projected (Forecast-view) delivery KPIs broken out by tier - the tier-level
// mirror of computeProjectedOTIFAC, used when the Reports toggle is on Forecast.
// count = completed + in-flight in the tier.
export function calculateProjectedKPIsByTier(
  completed: Project[],
  inFlight: Project[],
  onTimeDays = 14,
  atCostPct = 10,
  riskMinScore = DEFAULT_RISK_FORECAST_MIN_SCORE,
) {
  return TIERS.map(tier => ({
    tier,
    ...computeProjectedOTIFAC(
      completed.filter(p => p.tier === tier),
      inFlight.filter(p => p.tier === tier),
      onTimeDays,
      atCostPct,
      riskMinScore,
    ),
  }))
}

// Realised delivery KPIs broken out by sub-portfolio - used in Reports charts and Portfolio Summary.
// Callers MUST pass already-filtered (status==='complete') projects.
// hrsMode: 'owner'     = hours attributed to project.subPortfolio (default, existing behaviour)
//          'recipient' = hours split via project.valueSubPortfolios if set, else same as 'owner'
export function calculateRealisedKPIsByPortfolio(
  completed: Project[],
  subPortfolios: string[],
  onTimeDays = 14,
  atCostPct = 10,
  hrsMode: 'owner' | 'recipient' = 'owner',
) {
  const now = new Date()
  const curYear = now.getFullYear()
  const curMonth = now.getMonth() + 1
  return subPortfolios.map(portfolio => {
    // OTIF / At Cost always by project owner - you can't be on-time "for Finance"
    // and "for Operations" at the same time.
    const filtered = completed.filter(p => p.subPortfolio === portfolio)
    const hrsSaved = hrsMode === 'owner'
      ? filtered.reduce((sum, p) => sum + realisedLedgerHrs(p.monthlyLedger, curYear, curMonth), 0)
      : completed.reduce((sum, p) => sum + hrsForPortfolio(p, portfolio, curYear, curMonth), 0)
    return {
      portfolio,
      ...computeOTIFAC(filtered, onTimeDays, atCostPct),
      hrsSaved,
    }
  })
}

// Projected (Forecast-view) delivery KPIs by sub-portfolio. OTIFAC is projected
// (completed actuals blended with in-flight risk signals); hours saved stay
// realised (there's no "forecast hrs" in this table). count = completed +
// in-flight in the portfolio, so pipeline-only portfolios appear in Forecast.
export function calculateProjectedKPIsByPortfolio(
  completed: Project[],
  inFlight: Project[],
  subPortfolios: string[],
  onTimeDays = 14,
  atCostPct = 10,
  hrsMode: 'owner' | 'recipient' = 'owner',
  riskMinScore = DEFAULT_RISK_FORECAST_MIN_SCORE,
) {
  const now = new Date()
  const curYear = now.getFullYear()
  const curMonth = now.getMonth() + 1
  return subPortfolios.map(portfolio => {
    const comp = completed.filter(p => p.subPortfolio === portfolio)
    const flight = inFlight.filter(p => p.subPortfolio === portfolio)
    const hrsSaved = hrsMode === 'owner'
      ? comp.reduce((sum, p) => sum + realisedLedgerHrs(p.monthlyLedger, curYear, curMonth), 0)
      : completed.reduce((sum, p) => sum + hrsForPortfolio(p, portfolio, curYear, curMonth), 0)
    return {
      portfolio,
      ...computeProjectedOTIFAC(comp, flight, onTimeDays, atCostPct, riskMinScore),
      hrsSaved,
    }
  })
}

/** Sum only actual (past) ledger hours: prior years in full + current year up to current month. */
function realisedLedgerHrs(
  ledger: Record<string, MonthlyLedgerEntry> | undefined,
  curYear: number,
  curMonth: number,
): number {
  if (!ledger) return 0
  const yrs = new Set<number>()
  for (const key of Object.keys(ledger)) {
    const y = Number(key.slice(0, 4))
    if (!Number.isNaN(y)) yrs.add(y)
  }
  let total = 0
  for (const y of yrs) {
    if (y < curYear) total += yearTotal(ledger, y)
    else if (y === curYear) total += yearToDateTotal(ledger, y, curMonth)
    // future years are not realised
  }
  return total
}

/**
 * Realised ledger hours for a project attributed to a specific portfolio,
 * respecting the valueSubPortfolios split when present.
 * Absent split = 100% to project.subPortfolio (identical to existing behaviour).
 */
export function hrsForPortfolio(
  project: Project,
  portfolio: string,
  curYear: number,
  curMonth: number,
): number {
  const total = realisedLedgerHrs(project.monthlyLedger, curYear, curMonth)
  if (!project.valueSubPortfolios) {
    return project.subPortfolio === portfolio ? total : 0
  }
  return total * ((project.valueSubPortfolios[portfolio] ?? 0) / 100)
}

/**
 * Expected-value (forecast hrs) for a project attributed to a specific portfolio,
 * respecting the valueSubPortfolios split when present.
 */
export function forecastHrsForPortfolio(project: Project, portfolio: string): number {
  const ev = project.expectedValue || 0
  if (!project.valueSubPortfolios) {
    return project.subPortfolio === portfolio ? ev : 0
  }
  return ev * ((project.valueSubPortfolios[portfolio] ?? 0) / 100)
}

// Forecast pipeline broken out by sub-portfolio - used in the rebuilt
// Value Delivered chart (the "Forecast" series).
export function calculateForecastKPIsByPortfolio(
  forecast: Project[],
  subPortfolios: string[],
  hrsMode: 'owner' | 'recipient' = 'owner',
) {
  return subPortfolios.map(portfolio => {
    if (hrsMode === 'owner') {
      const filtered = forecast.filter(p => p.subPortfolio === portfolio)
      return {
        portfolio,
        pipelineHrs: filtered.reduce((sum, p) => sum + (p.expectedValue || 0), 0),
        expectedValue: filtered.reduce((sum, p) => sum + (p.expectedValue || 0), 0),
        projectCount: filtered.length,
      }
    }
    const pipelineHrs = forecast.reduce((sum, p) => sum + forecastHrsForPortfolio(p, portfolio), 0)
    return {
      portfolio,
      pipelineHrs,
      expectedValue: pipelineHrs,
      projectCount: forecast.filter(p => forecastHrsForPortfolio(p, portfolio) > 0).length,
    }
  })
}

// €34,500 format
export const formatCurrency = (v: number) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v)

// "22 Jan 2025"
export const formatDate = (d: string) =>
  new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

// "Today at 14:32" / "Yesterday at 09:15" / "20 Feb at 14:32"
export const formatDateTime = (d: string) => {
  const date = new Date(d)
  const now = new Date()
  const todayStr = now.toDateString()
  const yestStr = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).toDateString()
  const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  if (date.toDateString() === todayStr) return `Today at ${time}`
  if (date.toDateString() === yestStr) return `Yesterday at ${time}`
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ` at ${time}`
}

// Tier badge colours mapped to Danone brand palette:
// Black Line (top priority) = deep navy, Winning = Danone Blue,
// Stretch = brand orange, Licence to Operate = Process Cyan.
export function getTierColor(tier: string) {
  const map: Record<string, string> = {
    'Black Line':        'bg-[#002677] text-white',
    'Winning':           'bg-[#005EB8] text-white',
    'Stretch':           'bg-[#F88806] text-white',
    'License to Operate': 'bg-[#00ACED] text-white',
  }
  return map[tier] || 'bg-slate-500 text-white'
}

// Status badge: Forecast (in-flight = Danone Blue tint), Complete (success =
// Danone dark green tint), Cancelled (neutral grey).
export const statusConfig: Record<string, { label: string; style: string }> = {
  forecast:  { label: 'Forecast',  style: 'bg-[#005EB8]/10 text-[#005EB8]' },
  complete:  { label: 'Complete',  style: 'bg-[#00843D]/10 text-[#00843D]' },
  cancelled: { label: 'Cancelled', style: 'bg-slate-200 text-slate-600' },
}

// "12,345" - locale-aware integer formatting for headline figures.
export const formatNumber = (v: number) =>
  new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 }).format(v)

// Days between an ISO date and today, useful for "last check-in N days ago".
export const daysSince = (iso: string): number => {
  const ms = Date.now() - new Date(iso).getTime()
  return Math.max(0, Math.floor(ms / (24 * 60 * 60 * 1000)))
}

// "nayan.patel@danone.com" → "NP"
export const getInitials = (email: string) => {
  const name = email.split('@')[0]
  const parts = name.split('.')
  return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : name.slice(0, 2).toUpperCase()
}

// Returns "4.2" or null if no responses - used in tables
export const calculateAverageSatisfaction = (responses: SurveyResponse[]) => {
  if (responses.length === 0) return null
  return calculateAverageSatisfactionScore(responses).toFixed(1)
}

// Groups responses by month for the Satisfaction Trend chart.
// Returns { "2025-01" → [4.5, 3.0], "2025-02" → [4.0] }
export const groupResponsesByMonth = (responses: SurveyResponse[]) => {
  const byMonth = new Map<string, number[]>()
  responses.forEach(r => {
    const key = r.submittedAt.substring(0, 7) // "YYYY-MM"
    if (!byMonth.has(key)) byMonth.set(key, [])
    byMonth.get(key)!.push((r.techSatisfaction + r.peopleSatisfaction) / 2)
  })
  return byMonth
}

export const isValidEmail = (email: string) =>
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)
